import { useState } from "react";
import { Calendar as CalendarIcon, Clock, X } from "@phosphor-icons/react";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

/** Phase 238 — Reusable date + time picker.
 *
 * Renders a single trigger button showing the chosen date (and optional time).
 * Opens a popover with a real calendar (shadcn) plus an inline time input.
 * Both fields are stored separately on the parent (date as YYYY-MM-DD,
 * time as HH:MM) so the schema stays simple — the trigger formats them for
 * display.
 *
 * Props:
 *   date         — string "YYYY-MM-DD" (or "")
 *   time         — string "HH:MM" (or "")
 *   onDateChange — fn(YYYY-MM-DD)
 *   onTimeChange — optional fn(HH:MM); if omitted, time input is hidden
 *   label        — placeholder shown when no date set ("Pick a date")
 *   disabled     — bool
 *   required     — bool (just affects styling of the empty state)
 *   "data-testid"
 */
export default function DateTimePicker({
  date = "",
  time = "",
  onDateChange,
  onTimeChange,
  label = "Pick a date",
  disabled = false,
  required = false,
  className = "",
  "data-testid": testId = "datetime-picker",
}) {
  const [open, setOpen] = useState(false);
  const selected = date ? new Date(`${date}T00:00:00`) : undefined;

  const formatPretty = () => {
    if (!date) return label;
    const d = new Date(`${date}T00:00:00`);
    if (Number.isNaN(d.getTime())) return label;
    const datePart = d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
    return time ? `${datePart} · ${time}` : datePart;
  };

  return (
    <div className={`flex gap-1 ${className}`}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            disabled={disabled}
            className={`flex-1 justify-start font-normal h-9 text-xs sm:text-sm ${!date ? "text-muted-foreground" : ""} ${required && !date ? "border-amber-400" : ""}`}
            data-testid={testId}
          >
            <CalendarIcon size={14} className="mr-1.5 shrink-0" />
            <span className="truncate">{formatPretty()}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={selected}
            onSelect={(d) => {
              if (!d) { onDateChange?.(""); return; }
              const yyyy = d.getFullYear();
              const mm = String(d.getMonth() + 1).padStart(2, "0");
              const dd = String(d.getDate()).padStart(2, "0");
              onDateChange?.(`${yyyy}-${mm}-${dd}`);
              setOpen(false);
            }}
            initialFocus
          />
          {onTimeChange && (
            <div className="p-3 border-t border-border bg-muted/40 flex items-center gap-2">
              <Clock size={14} className="text-muted-foreground" />
              <Input
                type="time"
                value={time || ""}
                onChange={(e) => onTimeChange(e.target.value)}
                className="h-8 text-sm"
                data-testid={`${testId}-time`}
              />
              {time && (
                <Button type="button" variant="ghost" size="sm" onClick={() => onTimeChange("")} className="h-7 px-2" data-testid={`${testId}-time-clear`}>
                  <X size={12} />
                </Button>
              )}
            </div>
          )}
        </PopoverContent>
      </Popover>
      {date && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-9 w-9 shrink-0"
          onClick={() => { onDateChange?.(""); onTimeChange?.(""); }}
          aria-label="Clear date"
          data-testid={`${testId}-clear`}
        >
          <X size={14} />
        </Button>
      )}
    </div>
  );
}
